import { useEffect, useMemo, useState } from "react";

import { useNavigation } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { ChevronDown, Plus, Search } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CONTENT_TYPES,
  countryFlag,
} from "@/pages/tina/runs/components/site-picker/utils";
import { DiscoverTestDialog } from "@/pages/tina/sites/components/discover-test-dialog";
import { UnblockDialog } from "@/pages/tina/sites/components/unblock-dialog";
import { useSiteFacets } from "@/pages/tina/sites/hooks/use-site-facets";
import { useSitesColumns } from "@/pages/tina/sites/hooks/use-sites-columns";
import { useSitesRepository } from "@/pages/tina/sites/hooks/use-sites-repository";
import {
  type EnabledFilter,
  buildSiteFilters,
  isSourceGroup,
} from "@/pages/tina/sites/lib/site-filters";
import type { SiteRead } from "@/types/tina";

const ALL = "__all__";
const SEARCH_DEBOUNCE_MS = 300;

// The "Source" control: pick the whole "web" group (api/simple/sitemap) or
// "instagram" — both map to the backend `source_kind` param — or drill into a
// specific web strategy, which maps to an exact `x_strategy` filter.
const WEB_STRATEGIES = ["api", "simple", "sitemap"] as const;
const SOURCE_LABELS: Record<string, string> = {
  "": "All",
  web: "Web",
  instagram: "Instagram",
  facebook: "Facebook",
  api: "api",
  simple: "simple",
  sitemap: "sitemap",
};

export const SitesList = () => {
  const repo = useSitesRepository();
  const { create, edit } = useNavigation();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [contentType, setContentType] = useState("");
  const [source, setSource] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [enabled, setEnabled] = useState<EnabledFilter>("all");
  const [needsManualUnblock, setNeedsManualUnblock] = useState<
    "all" | "yes" | "no"
  >("all");
  const [discoverSite, setDiscoverSite] = useState<string | null>(null);
  const [unblockSite, setUnblockSite] = useState<string | null>(null);
  const [busyName, setBusyName] = useState<string | null>(null);

  // Debounce the search input so we don't refetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [search]);

  // Reset city when the country changes (a city belongs to its country).
  // Country and source/content-type are independent — changing one never
  // clears another, so filters can be set in any order without losing a prior
  // selection.
  useEffect(() => {
    setCity("");
  }, [country]);

  const enabledArg =
    enabled === "enabled" ? true : enabled === "disabled" ? false : undefined;

  const sourceIsGroup = isSourceGroup(source);
  const facets = useSiteFacets({
    contentType: contentType || undefined,
    sourceKind: sourceIsGroup ? source : undefined,
    xStrategy: !sourceIsGroup && source ? source : undefined,
    enabled: enabledArg,
    countryCode: country || undefined,
  });

  // Keep the active country/city visible in its dropdown even when the current
  // facet set (scoped by source/content-type) no longer lists it — otherwise
  // the trigger would show "All" while the filter is silently still applied.
  const countryOptions = useMemo(
    () =>
      country && !facets.countries.includes(country)
        ? [country, ...facets.countries]
        : facets.countries,
    [facets.countries, country],
  );
  const cityOptions = useMemo(
    () =>
      city && !facets.cities.includes(city)
        ? [city, ...facets.cities]
        : facets.cities,
    [facets.cities, city],
  );

  const permanent = useMemo(
    () =>
      buildSiteFilters({
        name: debouncedSearch,
        contentType,
        source,
        countryCode: country,
        city,
        enabled,
        needsManualUnblock,
      }),
    [
      debouncedSearch,
      contentType,
      source,
      country,
      city,
      enabled,
      needsManualUnblock,
    ],
  );

  const columns = useSitesColumns({
    onToggleEnabled,
    onDiscover: setDiscoverSite,
    onEdit: (name) => edit("tina-sites", name),
    onUnblock: setUnblockSite,
    busyName,
  });

  const table = useTable<SiteRead>({
    columns,
    refineCoreProps: {
      resource: "tina-sites",
      pagination: { pageSize: 25 },
      filters: { permanent },
    },
    meta: {
      onDoubleClick: (_e: React.MouseEvent, row: { original: SiteRead }) => {
        edit("tina-sites", row.original.name);
      },
    },
  });

  async function onToggleEnabled(name: string, next: boolean) {
    setBusyName(name);
    try {
      await repo.setEnabled(name, next);
      await table.refineCore.tableQuery.refetch();
    } finally {
      setBusyName(null);
    }
  }

  return (
    <div className="flex w-full max-w-none flex-col gap-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Sites</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse sites by content type, strategy and location, then edit or
            run a discover test.
          </p>
        </div>
        <Button onClick={() => create("tina-sites")}>
          <Plus className="mr-2 h-4 w-4" />
          Add site
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">Search</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name…"
              className="h-9 w-[220px] pl-8"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">Content type</Label>
          <Select
            value={contentType || ALL}
            onValueChange={(v) => setContentType(v === ALL ? "" : v)}
          >
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {CONTENT_TYPES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">Source</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-9 w-[140px] justify-between font-normal"
              >
                {SOURCE_LABELS[source] ?? "All"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[140px]">
              <DropdownMenuItem onSelect={() => setSource("")}>
                All
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Web</DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onSelect={() => setSource("web")}>
                      All web
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {WEB_STRATEGIES.map((s) => (
                      <DropdownMenuItem key={s} onSelect={() => setSource(s)}>
                        {s}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuItem onSelect={() => setSource("instagram")}>
                Instagram
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setSource("facebook")}>
                Facebook
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">Country</Label>
          <Select
            value={country || ALL}
            onValueChange={(v) => setCountry(v === ALL ? "" : v)}
          >
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {countryOptions.map((code) => {
                const flag = countryFlag(code);
                return (
                  <SelectItem key={code} value={code}>
                    {flag ? <span className="mr-1.5">{flag}</span> : null}
                    {code}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">City</Label>
          <Select
            value={city || ALL}
            onValueChange={(v) => setCity(v === ALL ? "" : v)}
            disabled={!country}
          >
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {cityOptions.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">Enabled</Label>
          <Select
            value={enabled}
            onValueChange={(v) => setEnabled(v as EnabledFilter)}
          >
            <SelectTrigger className="h-9 w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="enabled">Enabled</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium">Manual unblock</Label>
          <Select
            value={needsManualUnblock}
            onValueChange={(v) =>
              setNeedsManualUnblock(v as "all" | "yes" | "no")
            }
          >
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Needs unblock</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable table={table} />

      <DiscoverTestDialog
        siteName={discoverSite}
        open={discoverSite !== null}
        onOpenChange={(open) => {
          if (!open) setDiscoverSite(null);
        }}
      />

      <UnblockDialog
        siteName={unblockSite}
        open={unblockSite !== null}
        onOpenChange={(o) => {
          if (!o) setUnblockSite(null);
        }}
      />
    </div>
  );
};
